import { PARTY_ROLE_DISPLAY, type PartyRoleId, type PartyStatusId } from '@steerfi/shared';
import { IconChevronDown, IconChevronUp, IconPencil, IconTrash } from '@tabler/icons-react';
import type React from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ProviderApiRow } from '@/api/providers';
import {
  ROW_ACTIVATOR_ROW_CLASS,
  RowActivator,
  shouldIgnoreRowClick,
} from '@/components/row-activator';
import { getStatusColor, getStatusLabel } from '@/components/status-chip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { formatProviderDate } from '@/schemas/provider-form-schema';

type SortOrder = 'asc' | 'desc';

interface ProvidersTableProps {
  providers: ProviderApiRow[];
  isLoading: boolean;
  error: string | null;
  sortBy: string;
  order: SortOrder;
  onSort: (col: string, dir: SortOrder) => void;
  onEdit: (provider: ProviderApiRow) => void;
  onDelete: (provider: ProviderApiRow) => void;
}

interface HeaderColumn {
  key: string;
  label: string;
  /** Present iff the column is sortable; the key the API sorts by. */
  sortKey?: string;
  className?: string;
}

/**
 * The whole header row, not just its sortable part: `Role` sits between
 * `License #` and `Status`, so a sortable-only list can't drive the row and the
 * remaining heads end up hand-written and drifting apart.
 */
const COLUMNS: readonly HeaderColumn[] = [
  { key: 'provider_name', label: 'Name', sortKey: 'provider_name' },
  { key: 'provider_code', label: 'Code', sortKey: 'provider_code' },
  { key: 'license_number', label: 'License #', sortKey: 'license_number' },
  { key: 'party_role_id', label: 'Role' },
  { key: 'status_id', label: 'Status', sortKey: 'status_id' },
  { key: 'created_at', label: 'Created', sortKey: 'created_at' },
  { key: 'actions', label: 'Actions', className: 'text-right' },
];

/**
 * A column header whose label is a real button.
 *
 * It has to be a button rather than an `onClick` on the `<th>`: a `<th>` isn't
 * focusable, so the markup this replaced sorted by mouse only. `aria-sort`
 * stays on the `<th>` because that's where the `columnheader` role lives.
 *
 * Everything else -- the ghost `Button`, its classes, the chevron -- is copied
 * from `DataTable`'s sort control so this table renders identically to the
 * other thirteen. It used to be the only one with text arrows.
 */
function SortableHeader({
  label,
  sortKey,
  sortBy,
  order,
  onSort,
  className,
}: {
  label: string;
  sortKey: string;
  sortBy: string;
  order: SortOrder;
  onSort: (sortKey: string) => void;
  className?: string | undefined;
}) {
  const isActive = sortBy === sortKey;
  return (
    <TableHead
      aria-sort={isActive ? (order === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={cn('font-semibold', className)}
    >
      <Button
        type="button"
        variant="ghost"
        onClick={() => onSort(sortKey)}
        className="h-auto p-0 font-semibold"
      >
        {label}
        {isActive &&
          (order === 'asc' ? (
            <IconChevronUp className="ml-1 size-4" />
          ) : (
            <IconChevronDown className="ml-1 size-4" />
          ))}
      </Button>
    </TableHead>
  );
}

export const ProvidersTable: React.FC<ProvidersTableProps> = ({
  providers,
  isLoading,
  error,
  sortBy,
  order,
  onSort,
  onEdit,
  onDelete,
}) => {
  const navigate = useNavigate();
  const [sortAnnouncement, setSortAnnouncement] = useState('');

  const handleSort = (col: string) => {
    const nextOrder: SortOrder = sortBy === col && order === 'asc' ? 'desc' : 'asc';
    onSort(col, nextOrder);
    const label = COLUMNS.find((column) => column.sortKey === col)?.label ?? col;
    setSortAnnouncement(`Sorted by ${label}, ${nextOrder === 'asc' ? 'ascending' : 'descending'}.`);
  };

  if (error) {
    return (
      <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-destructive text-sm">
        {error}
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      {/*
        Sorting is server-side -- `onSort` triggers a refetch -- so the rows
        swap under the user with nothing to hear, and `aria-sort` only speaks
        when you navigate back to the header. Empty at first paint so mounting
        the page announces nothing.
      */}
      <output className="sr-only" aria-live="polite" aria-atomic="true">
        {sortAnnouncement}
      </output>
      <Table caption="Providers">
        <TableHeader>
          <TableRow>
            {COLUMNS.map((column) =>
              column.sortKey ? (
                <SortableHeader
                  key={column.key}
                  label={column.label}
                  sortKey={column.sortKey}
                  sortBy={sortBy}
                  order={order}
                  onSort={handleSort}
                  className={column.className}
                />
              ) : (
                <TableHead key={column.key} className={cn('font-semibold', column.className)}>
                  {column.label}
                </TableHead>
              )
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell
                colSpan={COLUMNS.length}
                className="text-center py-10 text-muted-foreground text-sm"
              >
                Loading providers…
              </TableCell>
            </TableRow>
          ) : providers.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={COLUMNS.length}
                className="text-center py-10 text-muted-foreground text-sm"
              >
                No providers match your filters.
              </TableCell>
            </TableRow>
          ) : (
            providers.map((p) => {
              const statusVariant = getStatusColor(p.status_id as PartyStatusId, 'party');
              const statusLabel = getStatusLabel(p.status_id as PartyStatusId, 'party');
              const roleName =
                PARTY_ROLE_DISPLAY[p.party_role_id as PartyRoleId] ?? String(p.party_role_id);
              return (
                <TableRow
                  key={p.party_id}
                  className={`cursor-pointer ${ROW_ACTIVATOR_ROW_CLASS}`}
                  onClick={(event) => {
                    if (shouldIgnoreRowClick(event)) return;
                    navigate(`/providers/${p.party_id}`);
                  }}
                >
                  {/* Deliberately not a `<th scope="row">`: the activator's
                      aria-label names an action, and a row header takes its
                      name from its contents, so every cell in the row would
                      announce "Open provider Acme". */}
                  <TableCell className="font-medium">
                    <RowActivator
                      link={{ to: `/providers/${p.party_id}` }}
                      label={`Open provider ${p.provider_name}`}
                    >
                      {p.provider_name}
                    </RowActivator>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {p.provider_code}
                  </TableCell>
                  <TableCell>{p.license_number ?? '—'}</TableCell>
                  <TableCell>{roleName}</TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={`text-xs font-medium ${
                        statusVariant === 'default'
                          ? 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800'
                          : statusVariant === 'destructive'
                            ? 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800'
                            : ''
                      }`}
                    >
                      {statusLabel}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatProviderDate(p.created_at)}</TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        aria-label={`Edit ${p.provider_name}`}
                        onClick={() => onEdit(p)}
                      >
                        <IconPencil size={14} aria-hidden />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        aria-label={`Delete ${p.provider_name}`}
                        onClick={() => onDelete(p)}
                      >
                        <IconTrash size={14} aria-hidden />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
};
